import React, { FC, useEffect, useState } from 'react';
import { Check } from 'react-feather';
import AppStoreButtons from 'components/AppStoreButtons';
import appPreview from 'images/instaacard/home/start-exploring/explore-1.png';

import { fetchHomeFeatureSection } from 'service/homeFeatureApi';

export interface SectionAppPromoProps {
    className?: string;
}

const APP_FEATURES = [
    'Update your profile',
    'Manage your contact information',
    'Add or edit social links',
    'Update your professional information',
    'Manage multiple profiles',
    'Keep your digital identity up to date',
    'Manage your INSTAACARD from one place'
];

const SectionAppPromo: FC<SectionAppPromoProps> = ({ className = '' }) => {
    /**
     * The hero image is editable from the admin panel (Content -> Home Features,
     * the 'App promo' row).
     *
     * It used to import explore-1.png directly -- the same file as the first item
     * of the feature grid -- so the two could never differ, and replacing that one
     * file silently changed both sections. They now have their own rows.
     *
     * The bundled import stays as the floor: it renders on the first frame and is
     * only replaced once a usable upload arrives, so a slow or failed content call
     * cannot leave a hole where the hero should be.
     */
    const [image, setImage] = useState<string>(appPreview);

    useEffect(() => {
        let cancelled = false;

        fetchHomeFeatureSection('app_promo').then((rows) => {
            const uploaded = rows[0]?.imageUrl;
            if (!cancelled && uploaded) setImage(uploaded);
        });

        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <div className={`nc-SectionAppPromo ${className}`} data-nc-id="SectionAppPromo">
            <div className="relative bg-icBlue-100 rounded-3xl overflow-hidden">
                {/* Decorative background shapes */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-32 -right-24 w-[460px] h-[460px] rounded-full bg-white/[0.06]"
                />
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -bottom-40 right-24 w-[360px] h-[360px] rounded-full bg-icYellow-100/10"
                />

                <div className="relative grid lg:grid-cols-12 items-center gap-12 lg:gap-8 px-6 py-14 sm:px-12 lg:pl-16 lg:pr-10 lg:py-16">
                    {/* Copy */}
                    <div className="lg:col-span-7 text-white">
                        <span className="inline-block rounded-full bg-icYellow-100 text-icBlue-100 text-xs font-bold uppercase tracking-wider px-3 py-1">
                            New
                        </span>
                        <h2 className="mt-5 font-semibold text-3xl sm:text-4xl xl:text-[2.75rem] !leading-[1.15] tracking-tight">
                            Meet the INSTAACARD App
                        </h2>
                        <p className="mt-4 text-xl sm:text-2xl font-medium text-icYellow-100">
                            Your INSTAACARD. Your Profile. Your Control.
                        </p>
                        <p className="mt-3 text-base text-white/75">Manage your digital profile anytime, anywhere.</p>

                        <div className="mt-9 border-t border-white/15 pt-8">
                            <p className="text-sm font-semibold uppercase tracking-wide text-white/70">
                                With the INSTAACARD App, you can
                            </p>
                            <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3.5">
                                {APP_FEATURES.map((feature) => (
                                    <li key={feature} className="flex items-start gap-3">
                                        <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-full bg-icYellow-100 text-icBlue-100">
                                            <Check size={13} strokeWidth={3.5} />
                                        </span>
                                        <span className="text-sm leading-6 text-white/90">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        <p className="mt-9 text-lg sm:text-xl font-semibold">Your connections don&apos;t need the app. You do.</p>

                        <AppStoreButtons className="mt-6" theme="light" />
                    </div>

                    {/* App preview */}
                    <div className="lg:col-span-5 flex justify-center lg:justify-end">
                        <img
                            src={image}
                            alt="The INSTAACARD app showing a digital profile being managed"
                            className="w-[220px] sm:w-[260px] lg:w-full lg:max-w-[300px] h-auto drop-shadow-2xl"
                            loading="lazy"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SectionAppPromo;
